import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../redux/actions';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import WastewaterPricingForm from './WastewaterPricingForm';
import InfoCard from './InfoCard';
import { singularUnitLabel } from '../utils/unitUtils';

class WastewaterPricing extends React.Component {
  state = {
    editingPrice: null,
    flashingIds: [],
    open: false,
    priceSearch: '',
  };

  componentWillUnmount() {
    if (this.flashTimeout) clearTimeout(this.flashTimeout);
  }

  prices() {
    return this.props.info?.wastewater_prices || [];
  }

  filterPrices() {
    const query = this.state.priceSearch.trim().toLowerCase();
    if (!query) return this.prices();

    return this.prices().filter((row) => {
      const haystack = [
        row.product_model,
        row.product_description,
        row.vendor_name,
        row.customer_name,
        row.notes,
        row.container_type,
        row.unit_of_measure,
        row.price != null ? this.formatPricePerUnit(row.price, row.unit_of_measure) : '',
        row.moq,
        row.username,
        this.formatDisplayDate(row.pricing_effective_start_date),
        this.formatDisplayDate(row.pricing_effective_end_date),
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(query);
    });
  }

  flashRows(ids) {
    if (!ids.length) return;

    if (this.flashTimeout) clearTimeout(this.flashTimeout);

    this.setState({ flashingIds: ids });
    this.flashTimeout = setTimeout(() => {
      this.setState({ flashingIds: [] });
      this.flashTimeout = null;
    }, 1400);
  }

  pricingSummary() {
    const prices = this.prices();
    if (!prices.length) return 'No records yet — open to add';

    const latest = prices[0];
    const parts = [
      latest.product_model,
      latest.customer_name,
      latest.price != null ? this.formatPricePerUnit(latest.price, latest.unit_of_measure) : null,
    ].filter(Boolean);

    return parts.join(' · ');
  }

  newPrice() {
    this.setState({
      open: true,
      editingPrice: {
        id: Date.now(),
        product_id: this.props.contextType === 'p' ? this.props.info?.id : '',
        customer_id: this.props.contextType === 'a' ? this.props.info?.id : '',
        selected_accounts: [],
        price: '',
        unit_of_measure: '',
        moq: '',
        container_type: '',
        pricing_effective_start_date: '',
        pricing_effective_end_date: '',
        notes: '',
        new: true,
        context_type: this.props.contextType,
        context_id: this.props.info?.id,
      },
    });
  }

  savePrice(price) {
    const existingIds = new Set(this.prices().map((p) => p.id));

    return this.props.updateWastewaterPrice(price, this.props.authToken, (json) => {
      const flashIds = price.new
        ? (json.wastewater_prices || []).filter((p) => !existingIds.has(p.id)).map((p) => p.id)
        : [price.id];

      this.setState({ editingPrice: null }, () => {
        this.flashRows(flashIds);
      });
    });
  }

  deletePrice(row) {
    this.props.updateWastewaterPrice(
      {
        ...row,
        _destroy: true,
        context_type: this.props.contextType,
        context_id: this.props.info?.id,
      },
      this.props.authToken,
      () => this.setState({ editingPrice: null })
    );
  }

  formatDisplayDate(value) {
    if (!value) return null;
    const [year, month, day] = value.split('-');
    if (!year || !month || !day) return value;
    return `${parseInt(month, 10)}/${parseInt(day, 10)}/${year}`;
  }

  formatPrice(value) {
    if (value == null || value === '') return null;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return num.toFixed(4);
  }

  formatPricePerUnit(price, unit) {
    const formatted = this.formatPrice(price);
    if (formatted == null) return null;
    const unitLabel = singularUnitLabel(unit);
    return unitLabel ? `$${formatted}/${unitLabel}` : `$${formatted}`;
  }

  renderPricePerUnit(price, unit) {
    const formatted = this.formatPrice(price);
    if (formatted == null) return '—';

    const unitLabel = singularUnitLabel(unit);
    if (!unitLabel) {
      return (
        <div className="ww-price-value">
          <span className="ww-price-amount">${formatted}</span>
        </div>
      );
    }

    return (
      <div className="ww-price-value">
        <span className="ww-price-amount">${formatted}</span>
        <span className="ww-price-unit">/{unitLabel}</span>
      </div>
    );
  }

  renderLookupLink(id, type, label) {
    if (!id || !label) return <span className="ww-price-link-empty">—</span>;

    return (
      <button
        type="button"
        className="ww-price-link"
        onClick={(e) => {
          e.stopPropagation();
          this.props.fetchInfo(this.props.authToken, id, type, null);
        }}
      >
        {label}
      </button>
    );
  }

  render() {
    const prices = this.prices();
    const filteredPrices = this.filterPrices();
    const { editingPrice, flashingIds } = this.state;
    const isEditing = !!editingPrice;
    const flashingSet = new Set(flashingIds);

    return (
      <InfoCard
        id="wastewater-pricing"
        icon={<AttachMoneyIcon />}
        title="Wastewater pricing"
        count={prices.length}
        subtitle="Product pricing by account"
        summary={this.pricingSummary()}
        open={this.state.open || isEditing}
        onOpenChange={(open) => this.setState({
          open,
          ...(open ? {} : { editingPrice: null, priceSearch: '' }),
        })}
      >
          {editingPrice && (
            <WastewaterPricingForm
              key={editingPrice.id}
              initialValues={editingPrice}
              contextType={this.props.contextType}
              info={this.props.info}
              accounts={this.props.accounts}
              products={this.props.products}
              onSave={(price) => this.savePrice(price)}
              onCancel={() => this.setState({ editingPrice: null })}
              onDelete={(row) => this.deletePrice(row)}
            />
          )}

          {prices.length > 0 && (
            <input
              type="text"
              placeholder="Search by product, account, vendor, price, notes..."
              value={this.state.priceSearch}
              onChange={(e) => this.setState({ priceSearch: e.target.value })}
              className="search-input ww-price-search"
            />
          )}

          {filteredPrices.length > 0 ? (
            <TableContainer component={Paper} className="ww-price-table-container">
              <Table className="ww-price-table" aria-label="wastewater pricing table" size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Product / Vendor / Account</TableCell>
                    <TableCell>Price / Unit</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrices.map((row) => (
                    <TableRow
                      key={`wastewater-price-${row.id}`}
                      className={[
                        editingPrice?.id === row.id ? 'ww-price-row-editing' : '',
                        flashingSet.has(row.id) ? 'ww-price-row-flash' : '',
                      ].filter(Boolean).join(' ')}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <div className="ww-price-cell-primary">
                          {this.renderLookupLink(row.product_id, 'p', row.product_model)}
                        </div>
                        <div className="ww-price-cell-secondary">
                          {this.renderLookupLink(row.vendor_id, 'v', row.vendor_name)}
                        </div>
                        <div className="ww-price-cell-secondary">
                          {this.renderLookupLink(row.customer_id, 'a', row.customer_name)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {this.renderPricePerUnit(row.price, row.unit_of_measure)}
                      </TableCell>
                      <TableCell>
                        {row.notes && <div className="ww-price-notes">{row.notes}</div>}
                        {row.moq != null && row.moq !== '' && (
                          <div className="ww-price-cell-secondary">
                            MOQ: {row.moq} {row.moq_unit_of_measure}
                          </div>
                        )}
                        {row.container_type && (
                          <div className="ww-price-cell-secondary">
                            Container: {row.container_type}
                          </div>
                        )}
                        {(row.pricing_effective_start_date || row.pricing_effective_end_date) && (
                          <div className="ww-price-cell-secondary">
                            Effective:{' '}
                            {[
                              this.formatDisplayDate(row.pricing_effective_start_date),
                              this.formatDisplayDate(row.pricing_effective_end_date),
                            ].filter(Boolean).join(' – ')}
                          </div>
                        )}
                        <div className="ww-price-meta">
                          {row.updated_at && <span>Updated {row.updated_at} by {row.username}</span>}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <button
                          type="button"
                          className="ww-price-edit-btn"
                          disabled={isEditing}
                          onClick={() => this.setState({ editingPrice: { ...row } })}
                        >
                          <EditIcon />
                          <span>Edit</span>
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            prices.length > 0 && !isEditing && (
              <div className="ww-price-empty">
                No pricing records match your search.
              </div>
            )
          )}

          {prices.length === 0 && !isEditing && (
            <div className="ww-price-empty">
              No pricing records yet. Add one to get started.
            </div>
          )}

          {!isEditing && (
            <div className="add-new" onClick={() => this.newPrice()}>
              <AddIcon />
              <span>Add pricing record</span>
            </div>
          )}
      </InfoCard>
    );
  }
}

function mapStateToProps(state) {
  return {
    authToken: state.authToken,
    info: state.info,
    products: state.products,
    accounts: state.accounts,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(Actions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(WastewaterPricing);
